/** Список анкет сотрудника (§16.5, экран /clients). */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { DraftSummary } from '../api/types'

const STATUS_LABELS: Record<string, string> = {
  draft: 'черновик',
  calculated: 'рассчитана',
  deal: 'сделка',
}

export function ClientsList() {
  const navigate = useNavigate()
  const [drafts, setDrafts] = useState<DraftSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [label, setLabel] = useState('')

  async function reload() {
    try {
      setDrafts(await api.listDrafts())
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function create() {
    setCreating(true)
    try {
      const { id } = await api.createDraft(label.trim() || undefined)
      navigate(`/intake/${id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreating(false)
    }
  }

  function open(draft: DraftSummary) {
    // Рассчитанную анкету и сделку открываем сразу на результате, черновик — на анкете.
    navigate(draft.статус === 'draft' ? `/intake/${draft.id}` : `/result/${draft.id}`)
  }

  async function rename(draft: DraftSummary) {
    const value = window.prompt('Подпись анкеты (без паспорта и телефона клиента)', draft.метка ?? '')
    if (value == null) return
    setError(null)
    try {
      const saved = await api.setDraftLabel(draft.id, value.trim() || null)
      setDrafts((prev) => prev.map((d) => (d.id === draft.id ? { ...d, метка: saved.метка } : d)))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function remove(draft: DraftSummary) {
    const name = draft.метка ?? 'без подписи'
    if (!window.confirm(`Удалить анкету «${name}»? Это нельзя отменить.`)) return
    setError(null)
    try {
      await api.deleteDraft(draft.id)
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div>
      <div className="card new-draft">
        <label htmlFor="new-draft-label">Подпись клиента — чтобы узнать анкету в списке</label>
        <div className="new-draft-row">
          <input
            id="new-draft-label"
            value={label}
            maxLength={255}
            placeholder="например: Иванова А., 2-комн. на Мира"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create()
            }}
          />
          <button className="primary" onClick={create} disabled={creating}>
            Новая анкета
          </button>
        </div>
        <p className="muted small-note">Паспорт, телефон и дату рождения клиента сюда не пишите.</p>
      </div>

      {error != null && <p className="error">{error}</p>}

      {drafts.length === 0 ? (
        <p className="muted">Анкет пока нет.</p>
      ) : (
        drafts.map((draft) => (
          <div key={draft.id} className="card">
            <div className="card-row">
              <div>
                <strong>{draft.метка ?? 'Без подписи'}</strong>
                <div className="muted">
                  {STATUS_LABELS[draft.статус] ?? draft.статус} · ответов: {draft.ответов} ·{' '}
                  {new Date(draft.обновлена).toLocaleDateString('ru-RU')}
                </div>
              </div>
              <div className="actions" style={{ margin: 0 }}>
                {draft.статус !== 'draft' && (
                  <button onClick={() => navigate(`/result/${draft.id}`)}>Результат</button>
                )}
                <button onClick={() => open(draft)}>Открыть</button>
                <button onClick={() => rename(draft)}>Подписать</button>
                <button onClick={() => remove(draft)}>Удалить</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}
